import {useState} from 'react';
import {SearchField, view} from '@sail/ui';
import {WalletSelectListEmpty} from 'src/v2/components/walletSelect/walletList/WalletSelectListEmpty';
import {WalletSelectListResults} from 'src/v2/components/walletSelect/walletList/WalletSelectListResults';
import {WalletSelectListSecondary} from 'src/v2/components/walletSelect/walletList/WalletSelectListSecondary';
import {Text} from 'src/v2/components/common';
import {getWalletOptionKey} from 'src/v2/components/walletSelect/getWalletOptionKey';
import type {PaymentOption} from 'src/v2/state/types';
import {message, useIntl} from '@sail/intl';

const MAX_SEARCH_RESULTS = 8;
const MAX_WALLETS_WITHOUT_SEARCH = 5;

interface Props {
  walletOptions: PaymentOption[];
  defaultWalletOptions: PaymentOption[];
  onWalletPress: (walletOption: PaymentOption) => void;
  onDirectDepositPress?: () => void;
  onConnectedWalletPress?: () => void;
}

const sortWalletOptionsByDefaultOrder = (
  walletOptions: PaymentOption[],
  defaultWalletOptions: PaymentOption[],
) => {
  const defaultWalletOptionOrder = new Map(
    defaultWalletOptions.map((walletOption, index) => [
      getWalletOptionKey(walletOption),
      index,
    ]),
  );

  return walletOptions
    .map((walletOption, index) => ({walletOption, index}))
    .sort((a, b) => {
      const orderA = defaultWalletOptionOrder.get(
        getWalletOptionKey(a.walletOption),
      );
      const orderB = defaultWalletOptionOrder.get(
        getWalletOptionKey(b.walletOption),
      );

      if (orderA === undefined && orderB === undefined) {
        return a.index - b.index;
      }

      if (orderA === undefined) {
        return 1;
      }

      if (orderB === undefined) {
        return -1;
      }

      return orderA - orderB;
    })
    .map(({walletOption}) => walletOption);
};

const messages = {
  selectWallet: message({
    id: 'crypto.payins.v2.wallet_select_list.title',
    description: 'Title for the wallet selection list',
    defaultMessage: 'Select wallet',
  }),
  searchPlaceholder: message({
    id: 'crypto.payins.v2.wallet_select_list.search_placeholder',
    description: 'Placeholder text for the search field in wallet select list',
    defaultMessage: 'Search through over 400 wallets',
  }),
};

export const WalletSelectList = ({
  walletOptions,
  defaultWalletOptions,
  onWalletPress,
  onDirectDepositPress,
  onConnectedWalletPress,
}: Props) => {
  const intl = useIntl();
  const [searchQuery, setSearchQuery] = useState('');

  const showSearch = walletOptions.length > MAX_WALLETS_WITHOUT_SEARCH;
  const isSearching = showSearch && searchQuery.length > 0;

  let displayedWallets: PaymentOption[];
  if (isSearching) {
    displayedWallets = walletOptions
      .filter(({name}) =>
        name.toLowerCase().includes(searchQuery.toLowerCase()),
      )
      .slice(0, MAX_SEARCH_RESULTS);
  } else if (!showSearch) {
    displayedWallets = sortWalletOptionsByDefaultOrder(
      walletOptions,
      defaultWalletOptions,
    );
  } else {
    displayedWallets = defaultWalletOptions;
  }

  const showSecondaryActions =
    !isSearching && (!!onDirectDepositPress || !!onConnectedWalletPress);

  return (
    <view.div css={{stack: 'y', gapY: 'space.150'}}>
      <Text type="bodyEmphasized">
        {intl.formatMessage(messages.selectWallet)}
      </Text>

      {showSearch ? (
        <SearchField
          label={intl.formatMessage(messages.selectWallet)}
          hiddenElements={['label']}
          placeholder={intl.formatMessage(messages.searchPlaceholder)}
          onInputChange={setSearchQuery}
          menuTrigger="manual"
          size="large"
          css={{width: 'fill'}}
        />
      ) : null}

      {displayedWallets.length > 0 ? (
        <>
          <WalletSelectListResults
            walletOptions={displayedWallets}
            onWalletPress={onWalletPress}
          />

          {showSecondaryActions ? (
            <WalletSelectListSecondary
              onDirectDepositPress={onDirectDepositPress}
              onConnectedWalletPress={onConnectedWalletPress}
            />
          ) : null}
        </>
      ) : (
        <view.div css={{paddingY: 'space.500'}}>
          <WalletSelectListEmpty onDirectDepositPress={onDirectDepositPress} />
        </view.div>
      )}
    </view.div>
  );
};
